import {
  AccountBalance,
  ArrowBack,
  Email as EmailIcon,
  FilterList,
  MoreVert,
  OpenInNew,
  Phone,
  Person,
  ReceiptLong,
  School,
  StickyNote2Outlined,
} from "@mui/icons-material";
import { CircularProgress, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import DeleteModal from "components/Modals/DeleteModal";
import NotesModal from "components/Modals/NotesModal";
import { deleteData } from "const/axios";
import { API_URL } from "const/constants";
import useKothar from "context/useKothar";
import { formatDisplayDate } from "utils/dateFormat";
import { useEffect, useMemo, useState } from "react";
import { AiFillDelete, AiFillEdit } from "react-icons/ai";
import { FaPlusCircle } from "react-icons/fa";
import { IoCalendarOutline, IoLocationOutline } from "react-icons/io5";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { hasPermission } from "utils/permissionUtils";
import AccountVerificationChip from "../Accounts/AccountVerificationChip";
import TaskModal from "../Tasks/TaskModal";
import AdmissionFiltersPanel, { EMPTY_ADMISSION_FILTERS } from "./AdmissionFiltersPanel";
import AdmissionStatusBadge from "./AdmissionStatusBadge";
import { fetchAdmissionIntakes, fetchAdmissionsByClientId } from "./admissionListApi";
import { useAdmissionDeletePreview } from "./AddAdmissions/admissionApi";
import { P } from "const/permissions";
import { parseAdmissionApplicationData } from "../Accounts/accountUtils";

const resolveAdmissionListIds = (item) => {
  const app = parseAdmissionApplicationData(item?.applicationData);
  return {
    courseId: item?.course ?? app?.course ?? null,
    universityId: item?.university ?? app?.university ?? null,
    intake: item?.intake ?? app?.intake ?? null,
    state: item?.state ?? app?.state ?? null,
  };
};

const matchesAdmissionFilters = (item, filters) => {
  const { courseId, universityId, intake } = resolveAdmissionListIds(item);
  if (filters.course && Number(courseId) !== Number(filters.course)) return false;
  if (filters.university && Number(universityId) !== Number(filters.university)) return false;
  if (filters.intake && intake !== filters.intake) return false;
  if (filters.status && item.status !== filters.status) return false;
  if (
    filters.accountVerificationStatus &&
    item.accountVerificationStatus !== filters.accountVerificationStatus
  ) {
    return false;
  }
  return true;
};

const MetaItem = ({ icon: Icon, children, href }) => {
  const content = (
    <>
      <Icon className="!text-[13px] shrink-0 opacity-70" />
      <span className="truncate">{children}</span>
    </>
  );
  const base = "inline-flex items-center gap-1 text-xs text-app-text-secondary min-w-0";

  return href ? (
    <a href={href} className={`${base} hover:text-primary transition-colors`}>
      {content}
    </a>
  ) : (
    <span className={base}>{content}</span>
  );
};

const ClientAdmissionsPage = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [{ token, courseList, uniData }, { refetchUniData, refetchCourseList, refetchStudent }] =
    useKothar();

  const [filters, setFilters] = useState(EMPTY_ADMISSION_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuItem, setMenuItem] = useState(null);
  const [openNotesModal, setOpenNotesModal] = useState({ state: false, id: null });
  const [isAssignModalOpen, setIsAssignModalOpen] = useState({ state: false, data: null });
  const [deleteTarget, setDeleteTarget] = useState({ state: false, id: null });

  useEffect(() => {
    if (!uniData?.length) refetchUniData();
    if (!courseList?.length) refetchCourseList();
  }, []);

  const clientProfileQuery = useQuery(
    ["admission-client-profile", clientId],
    async () => {
      const res = await axios.get(`${API_URL}/accounts/client/${clientId}`);
      return res?.data?.data?.clientData ?? null;
    },
    { enabled: !!clientId, refetchOnWindowFocus: false }
  );

  const admissionsQuery = useQuery(
    ["client-admissions", clientId],
    () => fetchAdmissionsByClientId(clientId),
    { enabled: !!clientId, refetchOnWindowFocus: false, refetchOnMount: "always" }
  );

  const deletePreviewQuery = useAdmissionDeletePreview(deleteTarget.id, deleteTarget.state);

  const intakesQuery = useQuery(["student-intakes"], fetchAdmissionIntakes, {
    refetchOnWindowFocus: false,
  });
  const intakeOptions = (intakesQuery.data ?? []).map((i) => ({ label: i, value: i }));

  const admissions = admissionsQuery.data ?? [];
  const filteredAdmissions = useMemo(
    () => admissions.filter((item) => matchesAdmissionFilters(item, filters)),
    [admissions, filters]
  );

  const clientProfile = clientProfileQuery.data;
  const client = clientProfile || admissions[0] || {};

  const clientName = client?.name || "Client";
  const clientEmail = client?.email;
  const clientPhone = client?.number;
  const passportNumber = client?.passportNumber;
  const caseOfficer = client?.caseOfficer;

  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        filters.university ||
          filters.course ||
          filters.intake ||
          filters.status ||
          filters.accountVerificationStatus
      ),
    [filters]
  );

  const openAdmission = (item) => {
    navigate(`/admin/admission/edit/${item.id}`, { state: { item } });
  };

  const handleViewAccount = async (item) => {
    try {
      const accountId = item?.clientAccountId;
      if (!accountId) {
        toast.info("No account is linked to this admission yet.");
        return;
      }
      const res = await axios.get(`${API_URL}/accounts/${accountId}`);
      const account = res?.data?.data;
      navigate(`/admin/account/add?clientId=${item?.clientId}`, {
        state: {
          item: {
            ...(account || {}),
            clientAccountId: accountId,
            hasAccountDetails: true,
            highlightStudentId: item?.id,
            clientData: account?.clientData || {
              id: item?.clientId,
              name: item?.name,
              email: item?.email,
              number: item?.number,
              address: item?.address,
              // One officer per client: prefer the client's over the entry-level copy.
              caseOfficer: client?.caseOfficer || item?.caseOfficer,
            },
          },
        },
      });
    } catch (err) {
      toast.error(err?.response?.data?.errorMessage || "Unable to open linked account");
    }
  };

  const handleSendEmail = (email) => {
    if (!email) return;
    window.open(`https://mail.google.com/mail/?view=cm&fs=1&to=${email}`, "_blank");
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuItem(null);
  };

  const handleDeleteAdmission = () => {
    deleteData(
      `${API_URL}/student/${deleteTarget.id}`,
      () => {
        setDeleteTarget({ state: false, id: null });
        admissionsQuery.refetch();
        refetchStudent?.();
        toast.success("Admission deleted");
      },
      token
    );
  };

  const loading = admissionsQuery.isFetching || clientProfileQuery.isFetching;
  const canManage = hasPermission(P.ADMISSIONS.CREATE);

  const openCardMenu = (event, item) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuItem(item);
  };

  return (
    <div className="mt-4 dashBody px-4 pb-12 max-w-5xl mx-auto w-full">
      <button
        type="button"
        onClick={() => navigate("/admin/admission")}
        className="mb-3 inline-flex items-center gap-1 text-xs font-medium text-app-text-secondary hover:text-primary transition-colors"
      >
        <ArrowBack className="!text-sm" />
        All clients
      </button>

      {/* Minimal client header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h1 className="text-lg font-bold text-app-text truncate">
            {clientName}
            {passportNumber && (
              <span className="ml-1.5 font-normal text-app-text-muted">· {passportNumber}</span>
            )}
          </h1>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
            {clientEmail && (
              <MetaItem icon={EmailIcon} href={`mailto:${clientEmail}`}>
                {clientEmail}
              </MetaItem>
            )}
            {clientPhone && <MetaItem icon={Phone}>{clientPhone}</MetaItem>}
            {caseOfficer && <MetaItem icon={Person}>{caseOfficer}</MetaItem>}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => navigate(`/admin/client/view/${clientId}`)}
            className="inline-flex items-center gap-1 rounded-md border border-app-border bg-app-surface px-3 py-1.5 text-xs font-medium text-app-text hover:bg-app-surface-muted transition-colors"
          >
            Client profile
            <OpenInNew className="!text-[13px]" />
          </button>
          <Link
            to="/admin/admission/add"
            state={{ preselectClientId: Number(clientId), preselectClient: client }}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 transition-opacity"
          >
            <FaPlusCircle />
            New admission
          </Link>
        </div>
      </div>

      {/* Admissions list */}
      <section className="rounded-lg border border-app-border bg-app-surface overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-app-border bg-app-surface-muted/40">
          <h2 className="text-sm font-semibold text-app-text">
            Admissions
            <span className="ml-1.5 font-normal text-app-text-muted">
              {filteredAdmissions.length !== admissions.length
                ? `${filteredAdmissions.length} of ${admissions.length}`
                : admissions.length}
            </span>
          </h2>
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${
              showFilters || hasActiveFilters
                ? "border-primary bg-primary/10 text-primary"
                : "border-app-border text-app-text-secondary hover:bg-app-surface"
            }`}
          >
            <FilterList className="!text-[15px]" />
            Filters
            {hasActiveFilters && !showFilters && (
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            )}
          </button>
        </div>

        {showFilters && (
          <div className="border-b border-app-border bg-app-surface-muted/20">
            <AdmissionFiltersPanel
              embedded
              mode="admissions"
              filters={filters}
              setFilters={setFilters}
              uniData={uniData}
              courseList={courseList}
              intakeOptions={intakeOptions}
            />
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-app-text-secondary">
            <CircularProgress size={20} />
            Loading admissions…
          </div>
        ) : admissions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 px-6 text-center">
            <School className="!text-4xl text-app-text-muted mb-2" />
            <p className="text-sm font-medium text-app-text">No admissions yet</p>
            <Link
              to="/admin/admission/add"
              state={{ preselectClientId: Number(clientId), preselectClient: client }}
              className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 transition-opacity"
            >
              <FaPlusCircle />
              Create first admission
            </Link>
          </div>
        ) : filteredAdmissions.length === 0 ? (
          <div className="py-14 text-center">
            <p className="text-sm text-app-text-secondary">No admissions match the filters.</p>
            <button
              type="button"
              onClick={() => setFilters(EMPTY_ADMISSION_FILTERS)}
              className="mt-2 text-xs font-medium text-primary hover:underline"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <ul className="divide-y divide-app-border">
            {filteredAdmissions.map((item) => {
              const { courseId, universityId, intake, state } = resolveAdmissionListIds(item);
              const courseName =
                courseList?.find((c) => Number(c.courseId) === Number(courseId))?.courseName ||
                "Course not set";
              const universityName =
                uniData?.find((u) => Number(u.id) === Number(universityId))?.name ||
                "University not set";
              return (
                <li
                  key={item.id}
                  onClick={() => openAdmission(item)}
                  className="group flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-app-surface-hover transition-colors"
                >
                  {/* Course + university */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-semibold text-sm text-app-text truncate group-hover:text-primary transition-colors">
                        {courseName}
                      </span>
                      {item.accountVerificationStatus && (
                        <AccountVerificationChip
                          status={item.accountVerificationStatus}
                          rejectionFeedback={item.accountVerificationFeedback}
                        />
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-app-text-secondary mt-0.5">
                      <span className="inline-flex items-center gap-1 truncate">
                        <School className="!text-[13px] opacity-70 shrink-0" />
                        {universityName}
                      </span>
                      {intake && (
                        <span className="inline-flex items-center gap-1">
                          <IoCalendarOutline className="opacity-70" />
                          {intake}
                        </span>
                      )}
                      {state && (
                        <span className="inline-flex items-center gap-1">
                          <IoLocationOutline className="opacity-70" />
                          {state}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Status */}
                  <div className="shrink-0 hidden sm:block">
                    <AdmissionStatusBadge status={item.status} />
                  </div>

                  {/* Updated */}
                  <span className="shrink-0 hidden lg:block w-28 text-right text-xs text-app-text-muted">
                    {formatDisplayDate(item.updatedDate || item.createdDate)}
                  </span>

                  {/* Actions */}
                  <div className="shrink-0 flex items-center" onClick={(e) => e.stopPropagation()}>
                    {item?.hasAdmissionAccount && item?.clientAccountId && (
                      <Tooltip title="View account">
                        <IconButton size="small" onClick={() => handleViewAccount(item)}>
                          <AccountBalance fontSize="small" className="!text-primary" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Fee details">
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate(`/admin/admission/fees/${item.id}`, { state: { item } })
                        }
                      >
                        <ReceiptLong fontSize="small" className="!text-app-text-secondary" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="More actions">
                      <IconButton
                        size="small"
                        aria-label="Admission actions"
                        onClick={(e) => openCardMenu(e, item)}
                        className="!text-app-text-secondary"
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        PaperProps={{
          className: "rounded-xl border border-app-border shadow-lg min-w-[200px] mt-1",
        }}
      >
        {menuItem && [
          <MenuItem
            key="edit"
            onClick={() => {
              openAdmission(menuItem);
              closeMenu();
            }}
            className="!text-sm gap-2"
          >
            <AiFillEdit className="text-primary" />
            Edit admission
          </MenuItem>,
          <MenuItem
            key="notes"
            onClick={() => {
              setOpenNotesModal({ state: true, id: menuItem.id });
              closeMenu();
            }}
            className="!text-sm gap-2"
          >
            <StickyNote2Outlined fontSize="small" className="!text-amber-600" />
            Notes
          </MenuItem>,
          canManage ? (
            <MenuItem
              key="task"
              onClick={() => {
                setIsAssignModalOpen({ state: true, data: menuItem });
                closeMenu();
              }}
              className="!text-sm gap-2"
            >
              <FaPlusCircle className="text-green-600" />
              Assign task
            </MenuItem>
          ) : null,
          menuItem?.hasAdmissionAccount && menuItem?.clientAccountId ? (
            <MenuItem
              key="account"
              onClick={() => {
                handleViewAccount(menuItem);
                closeMenu();
              }}
              className="!text-sm gap-2"
            >
              <AccountBalance fontSize="small" className="!text-primary" />
              View account
            </MenuItem>
          ) : null,
          <MenuItem
            key="fees"
            onClick={() => {
              navigate(`/admin/admission/fees/${menuItem.id}`, { state: { item: menuItem } });
              closeMenu();
            }}
            className="!text-sm gap-2"
          >
            <ReceiptLong fontSize="small" />
            Fee details
          </MenuItem>,
          <MenuItem
            key="email"
            onClick={() => {
              handleSendEmail(menuItem?.email || clientEmail);
              closeMenu();
            }}
            className="!text-sm gap-2"
          >
            <EmailIcon fontSize="small" className="!text-primary" />
            Send email
          </MenuItem>,
          canManage ? (
            <MenuItem
              key="delete"
              onClick={() => {
                setDeleteTarget({ state: true, id: menuItem.id });
                closeMenu();
              }}
              className="!text-sm gap-2 !text-red-600"
            >
              <AiFillDelete />
              Delete admission
            </MenuItem>
          ) : null,
        ]}
      </Menu>

      {isAssignModalOpen?.state && (
        <TaskModal
          open={isAssignModalOpen.state}
          modalData={isAssignModalOpen.data}
          handleClose={() => setIsAssignModalOpen({ state: false, data: null })}
          module="Admission"
          admissionId={isAssignModalOpen.data?.id ?? null}
          moduleEntryType="ADMISSION"
          moduleEntryId={isAssignModalOpen.data?.id ?? null}
        />
      )}
      {openNotesModal?.state && (
        <NotesModal
          open={openNotesModal.state}
          handleClose={() => setOpenNotesModal({ state: false, id: null })}
          studentId={openNotesModal.id}
          type="student"
        />
      )}
      {deleteTarget.state && (
        <DeleteModal
          open={deleteTarget.state}
          item="Student Information"
          handleCancel={() => setDeleteTarget({ state: false, id: null })}
          handleDelete={handleDeleteAdmission}
          preview={deletePreviewQuery.data ?? null}
          previewLoading={deletePreviewQuery.isFetching}
        />
      )}
    </div>
  );
};

export default ClientAdmissionsPage;
